import { ApolloError, gql, useMutation, useQuery } from '@apollo/client'
import numeral from 'numeral'
import { useEffect, useMemo, useState } from 'react'

import {
  FirstComeFirstServedConfirmationFragmentDoc,
  FirstComeFirstServedConfirmationQuery,
  FirstComeFirstServedConfirmationQueryVariables,
  PaymentDetailFragmentDoc,
  PaymentMethod,
  PurchaseFirstComeFirstServedMutation,
  PurchaseFirstComeFirstServedMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config, constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { App } from '@/core/native'
import {
  getPrefrenceObject,
  getPrefrenceString,
  removePrefernce,
  setPreference,
} from '@/core/native/storage'
import { generateTransactionId, useAnalytics } from '@/lib/analytics'
import { createUuid } from '@/lib/uuid'

import { ApplyPreSaleLotteryValue } from '../LotteryDetail/template/LotteryDetailForm'
import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('firstComeFirstServedConfirmation')
  const { user } = useCurrentUser()
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const { router, generatePath, routerPages } = useRouter<{
    preSaleId: string
  }>()
  const [choice, setChoice] = useState<ApplyPreSaleLotteryValue>()
  const { data, loading } = useQuery<
    FirstComeFirstServedConfirmationQuery,
    FirstComeFirstServedConfirmationQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.preSaleId,
    },
  })
  const [purchaseFirstComeFirstServed, { loading: submitting }] = useMutation<
    PurchaseFirstComeFirstServedMutation,
    PurchaseFirstComeFirstServedMutationVariables
  >(PURCHASE_FIRST_COME_FIRST_SERVED)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  useEffect(() => {
    ;(async () => {
      const v = await getPrefrenceObject<ApplyPreSaleLotteryValue>(
        'preSaleLotteryApplicationInput'
      )

      // localStorageにデータがない場合、購入ページを表示
      if (v.preSaleId !== router.query.preSaleId) {
        router.replace(
          generatePath(routerPages.FirstComeFirstServedDetail, {
            preSaleId: router.query.preSaleId,
          })
        )
      }
      // 画面ロード時に冪等キーがなければ作成する
      if (!(await getPrefrenceString('idempotencyKey'))) {
        await setPreference('idempotencyKey', createUuid())
      }

      setChoice(v)
    })()
  }, [getPrefrenceObject])

  const isKonbini = useMemo(
    () => choice?.paymentMethod === PaymentMethod.Konbini,
    [choice?.paymentMethod]
  )
  const billingStatements = useMemo(() => {
    // 代金や手数料など、支払のすべてをブレイクダウンした配列
    return choice?.choices?.map(({ itemId, skuId }, index) => {
      const item = data?.preSale?.items?.find(i => i?.id === itemId)
      const itemPrice = skuId
        ? item?.skuSettings?.units?.find(s => s?.id === skuId)?.price ??
          item?.price
        : item?.price
      const purchaseCount = choice?.purchaseCountChoices?.[index]?.count ?? 0

      // 代金や手数料など、支払のすべてをブレイクダウンした配列
      const statements = [
        {
          label: t('totalPrice'),
          value: `￥${numeral((itemPrice ?? 0) * purchaseCount).format('0,0')}`,
        },
        ...(item?.fees?.collections || []).map(fee => {
          const feeValue = fee?.fee ?? 0
          return {
            label: fee?.feeName,
            value:
              fee?.per === 'order'
                ? `￥${numeral(feeValue).format('0,0')}`
                : `￥${numeral(feeValue * purchaseCount).format('0,0')}`,
          }
        }),
        ...(isKonbini
          ? [
              {
                label: item?.fees?.konbini?.feeName,
                value: `￥${numeral(item?.fees?.konbini?.fee).format('0,0')}`,
              },
            ]
          : []),
      ]
      const totalFee = item?.fees?.collections?.reduce(
        (acc, fee) =>
          acc +
          (fee?.per === 'order'
            ? fee?.fee ?? 0
            : (fee?.fee ?? 0) * purchaseCount),
        0
      )
      return {
        statements,
        totalPrice:
          (itemPrice ?? 0) * purchaseCount +
          (totalFee ?? 0) +
          (isKonbini ? item?.fees?.konbini?.fee ?? 0 : 0),
      }
    })
  }, [
    choice?.choices,
    choice?.purchaseCountChoices,
    data?.preSale?.items,
    isKonbini,
    t,
  ])

  const item = useMemo(() => {
    return data?.preSale?.items.find(
      item => item.id === choice?.choices[0].itemId
    )
  }, [choice?.choices, data?.preSale?.items])

  return {
    state: {
      loading,
      preSale: data?.preSale,
      paymentSource: data?.paymentSource,
      paymentMethod: choice?.paymentMethod ?? PaymentMethod.CreditCard,
      billingStatements,
      choice,
      submitting,
      openExpiredCardModal,
      openPaymentErrorModal,
      openRequire3DSecureModal,
    },
    actions: {
      async onSubmit() {
        if (!choice) return
        const idempotencyKey = await getPrefrenceString('idempotencyKey')
        if (!idempotencyKey) {
          // 冪等キーがなければエラーを表示する
          showError(t('error.idempotencyKeyNotFound'))
          return
        }
        await analytics?.track('begin_checkout', {
          item_list_id: `preSale_list_${router.query.preSaleId}`,
          item_list_name: 'PreSale Items',
          type: isKonbini ? 'convenience_store' : 'credit_card',
          value: billingStatements?.[0].totalPrice ?? 0,
          items: [
            {
              item_id: item?.id,
              item_name: item?.title,
              item_category: ConversionCategories.preSaleItem,
              item_category2: item?.type,
              item_category3: App.getPlatform(),
              item_category4: choice?.choices?.[0]?.skuId,
              item_category5: router.query.preSaleId,
              price: item?.price,
              quantity: choice?.purchaseCountChoices[0].count,
            },
          ],
        })

        await purchaseFirstComeFirstServed({
          variables: {
            inputs: {
              itemId: choice?.choices[0].itemId,
              skuId: choice?.choices[0].skuId,
              count: choice?.purchaseCountChoices[0].count,
              price: billingStatements?.[0].totalPrice ?? 0,
              preSaleId: router.query.preSaleId,
              isKonbini,
              fullName: isKonbini ? choice?.fullName : undefined,
            },
          },
          context: {
            headers: { 'x-utoniq-idempotency-key': idempotencyKey },
          },
        })
          .then(async ({ data }) => {
            await removePrefernce('preSaleLotteryApplicationInput')

            await analytics?.ecommerce({
              transaction_id: generateTransactionId(user?.uid),
              currency: 'JPY',
              type: isKonbini ? 'convenience_store' : 'credit_card',
              value: billingStatements?.[0].totalPrice ?? 0,
              items: [
                {
                  item_id: item?.id ?? '',
                  item_name: item?.title ?? '',
                  item_category: ConversionCategories.preSaleItem,
                  item_category2: item?.type,
                  item_category3: App.getPlatform(),
                  item_category4: 'FirstComeFirstServedConfirmation',
                  item_category5: router.query.preSaleId,
                  price: item?.price ?? 0,
                  quantity: choice?.purchaseCountChoices[0].count,
                },
              ],
            })

            router.replace(
              generatePath(routerPages.FirstComeFirstServedComplete, {
                cartTransactionOrderId: data?.purchaseItemImmediately.id ?? '#',
                preSaleId: router.query.preSaleId,
              })
            )
          })
          .catch(async (e: ApolloError) => {
            analytics?.track('error_preSaleCheckout', {
              applicationKey: config.applicationKey,
              preSaleId: router.query.preSaleId,
              error: e?.name,
              errorCode: e?.graphQLErrors?.[0]?.extensions?.errorCode,
            })
            switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
              /**
               * @description
               * 3Dセキュア認証が必要な場合
               */
              case constants.errorCodes.REQUIRE_3D_SECURE:
                setOpenRequire3DSecureModal(true)
                // リトライのために冪等キーを更新
                await setPreference('idempotencyKey', createUuid())
                return
              /**
               * @desciption
               * カードが有効期限切れの場合
               */
              case constants.errorCodes.EXPIRED_CARD:
                setOpenExpiredCardModal(true)
                // リトライのために冪等キーを更新
                await setPreference('idempotencyKey', createUuid())
                return
              /**
               * @desciption
               * 何かしらの決済エラーの場合
               */
              case constants.errorCodes.PAYMENT_ERROR:
                setOpenPaymentErrorModal(true)
                // リトライのために冪等キーを更新
                await setPreference('idempotencyKey', createUuid())
                return
            }
            // 指定枚数分連番の座席がない場合、その旨のエラーを表示
            showError(
              e?.graphQLErrors?.flatMap(({ extensions }) =>
                extensions?.exception?.errors?.map(
                  (error: { metadata: ApolloError }) => error?.metadata
                )
              )?.[0]?.userMessage ?? e
            )
          })
      },
      onCloseRequire3DSecureModal() {
        setOpenRequire3DSecureModal(false)
      },
      onCloseExpiredCardModal() {
        setOpenExpiredCardModal(false)
      },
      onClosePaymentErrorModal() {
        setOpenPaymentErrorModal(false)
      },
    },
  }
})

const QUERY = gql`
  query FirstComeFirstServedConfirmation($id: ID!) {
    preSale(id: $id) {
      ...FirstComeFirstServedConfirmation
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${FirstComeFirstServedConfirmationFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const PURCHASE_FIRST_COME_FIRST_SERVED = gql`
  mutation PurchaseFirstComeFirstServed(
    $inputs: PurchaseItemImmediatelyInput!
  ) {
    purchaseItemImmediately(inputs: $inputs) {
      id
      paymentType
    }
  }
`
